import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { NavigableError } from 'services/errorsNavigation/errorsNavigation';
import { EMPTY_FIELD } from 'support/helpers/const/const';

import { Button } from '../Button/Button';

type DataDisplayProps<T extends NavigableError> = {
  label: string | undefined;
  value: string | number | undefined;
  error?: T;
  totalNumberOfErrors: number;
  fieldId: string;
  onShowFieldErrorModal: (showFieldErrorModal: boolean) => void;
  description: string | undefined;
};

const isRequiredError = (error: NavigableError | undefined) => {
  if (!error) {
    return false;
  }
  return error.type === 'required';
};

export const DataDisplay = <T extends NavigableError>({
  label,
  value,
  error,
  totalNumberOfErrors,
  fieldId,
  onShowFieldErrorModal,
  description,
}: DataDisplayProps<T>) => {
  const { t } = useTranslation();
  const { ErrorModalComponent, useErrorModal } = useErrorContext();
  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
  const valueWithFallback = value || <span className="text-gray-400">{EMPTY_FIELD} </span>;
  const buttonText = isRequiredError(error) ? t('webedi:errors.required.placeholder') : valueWithFallback;

  useEffect(() => {
    if (error && showErrorModal) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, onShowFieldErrorModal, showErrorModal]);

  return (
    <div>
      <div className="flex flex-col gap-2">
        <span className="text-wrap text-xs text-gray-500">{label}</span>
        {error ? (
          <div className="flex w-fit items-center gap-1" ref={refs.setReference}>
            <Button
              variant="text"
              id={fieldId}
              data-field={fieldId}
              className="!focus-visible:ring-0 !focus-visible:outline-none -ml-1 -mt-1.5 hyphens-auto border border-transparent !p-1 !pr-1.5 text-sm !font-normal leading-tight text-red-700 underline !ring-0 hover:text-red-800 focus:border-red-500 focus:bg-red-50"
              onClick={() => setShowErrorModal(true)}
              onFocus={() => setShowErrorModal(true)}
              onBlur={() => setShowErrorModal(false)}
              RightIcon={ExclamationCircleIcon}
              analyticsId="field_display_error"
            >
              {buttonText}
            </Button>
          </div>
        ) : (
          <span className="hyphens-auto break-words text-sm text-gray-700">{valueWithFallback}</span>
        )}
      </div>
      {showErrorModal && ErrorModalComponent ? (
        <div>
          <ErrorModalComponent
            viewMode={true}
            error={error}
            numberOfErrors={totalNumberOfErrors}
            ref={refs.setFloating}
            style={floatingStyles}
            fieldDescription={description}
          />
        </div>
      ) : null}
    </div>
  );
};
